import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { format, parseISO } from 'date-fns';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { useMediaQuery } from 'react-responsive';
import {
  Box, Container, Typography, Paper, Grid, Chip, Button, CircularProgress,
  FormControl, InputLabel, Select, MenuItem, TextField, Alert, Collapse,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Drawer, IconButton, Divider, Autocomplete,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import HomeRepairServiceIcon from '@mui/icons-material/HomeRepairService';

import { useUserAuth } from '../../context/UserAuthContext';
import Header from '../../components/Header/Header';
import Logo from '../../components/Logo/Logo';

function fmtDate(v) {
  if (!v) return '—';
  try { return format(typeof v === 'string' ? parseISO(v) : v, 'MMM d, yyyy'); }
  catch { return v; }
}

function fmtDateTime(v) {
  if (!v) return '—';
  try { return format(typeof v === 'string' ? parseISO(v) : v, 'MMM d, yyyy p'); }
  catch { return v; }
}

const STATUS_COLORS = {
  Open: 'default',
  'In Progress': 'info',
  Completed: 'success',
  Deferred: 'warning',
};

function StatusChip({ status }) {
  return <Chip size="small" label={status || 'Open'} color={STATUS_COLORS[status] || 'default'} variant={status === 'Open' ? 'outlined' : 'filled'} />;
}

// Days between when an issue was first flagged (walkthrough businessDate) and
// today. Returns null when the businessDate is missing or malformed.
export function daysOpenFrom(businessDate) {
  if (!businessDate) return null;
  const d = businessDate instanceof Date ? businessDate : new Date(businessDate);
  if (Number.isNaN(d.getTime())) return null;
  const ms = Date.now() - d.getTime();
  return Math.max(0, Math.floor(ms / (1000 * 60 * 60 * 24)));
}

// Green under 7 days, yellow at 7-13, red at 14+. Completed items get a
// muted grey chip regardless of age since they're no longer "open."
export function DaysOpenBadge({ businessDate, status }) {
  const days = daysOpenFrom(businessDate);
  if (days === null) return <span>—</span>;
  if (status === 'Completed') {
    return <Chip size="small" label={`${days}d`} variant="outlined" />;
  }
  const bg = days >= 14 ? '#c62828' : days >= 7 ? '#f57c00' : '#2e7d32';
  return (
    <Chip
      size="small"
      label={`${days}d`}
      sx={{ bgcolor: bg, color: 'white', fontWeight: 600, minWidth: 44 }}
    />
  );
}

function ItemsTable({ title, rows, onOpen, ownerColor, showStore }) {
  const [expanded, setExpanded] = useState(true);
  return (
    <Paper variant="outlined" sx={{ mb: 3 }}>
      <Box
        role="button"
        tabIndex={0}
        onClick={() => setExpanded((v) => !v)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setExpanded((v) => !v); } }}
        sx={{
          bgcolor: ownerColor,
          color: 'white',
          px: 2, py: 1.5,
          borderTopLeftRadius: 4,
          borderTopRightRadius: 4,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': { filter: 'brightness(1.05)' },
        }}
      >
        <ExpandMoreIcon
          sx={{
            transition: 'transform 200ms',
            transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
          }}
        />
        <Typography variant="subtitle1" fontWeight={600} sx={{ flex: 1 }}>
          {title}
        </Typography>
        <Chip
          size="small"
          label={rows.length}
          sx={{ bgcolor: 'rgba(255,255,255,0.25)', color: 'white', fontWeight: 600 }}
        />
      </Box>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        {rows.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">No open items.</Typography>
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 520 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  {showStore && <TableCell>Store</TableCell>}
                  <TableCell>Area</TableCell>
                  <TableCell>Item</TableCell>
                  <TableCell>Issue</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align="center">Days open</TableCell>
                  <TableCell>ETA</TableCell>
                  <TableCell>Last update</TableCell>
                  <TableCell align="right">Photos</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((row) => (
                  <TableRow key={row.frID} hover>
                    {showStore && <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.storeName || '—'}</TableCell>}
                    <TableCell>{row.area || '—'}</TableCell>
                    <TableCell>{row.item || '—'}</TableCell>
                    <TableCell sx={{ maxWidth: 320 }}>{row.issue}</TableCell>
                    <TableCell><StatusChip status={row.status} /></TableCell>
                    <TableCell align="center"><DaysOpenBadge businessDate={row.businessDate} status={row.status} /></TableCell>
                    <TableCell>{fmtDate(row.estimatedCorrectionDate)}</TableCell>
                    <TableCell>{fmtDateTime(row.actionUpdatedAt)}</TableCell>
                    <TableCell align="right">{row.imageCount || 0}</TableCell>
                    <TableCell align="right">
                      <Button size="small" variant="outlined" onClick={() => onOpen(row.frID)}>Update</Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Collapse>
    </Paper>
  );
}

// Renders a labeled row of image thumbnails. Skips rendering entirely if
// there are no images (so empty "Completion photos" heading doesn't appear
// on items that haven't been closed yet).
function PhotoGroup({ label, images }) {
  if (!images || images.length === 0) return null;
  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography variant="caption" color="text.secondary" fontWeight={600} display="block" sx={{ mb: 0.5 }}>
        {label} ({images.length})
      </Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {images.map((img) => (
          <a key={img.id} href={img.gcsUrl} target="_blank" rel="noreferrer">
            <img src={img.gcsUrl} alt={img.fileName} style={{ width: 96, height: 96, objectFit: 'cover', borderRadius: 4, border: '1px solid #e0e0e0' }} />
          </a>
        ))}
      </Box>
    </Box>
  );
}

// File input for completion photos, shown only when marking status=Completed.
// Selected files stay in local state until Submit posts them alongside the
// status change.
function CompletionPhotoInput({ files, onChange }) {
  return (
    <Box sx={{ border: '1px dashed', borderColor: 'primary.light', borderRadius: 1, p: 1.5, bgcolor: 'primary.50' }}>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        Suggested: attach a photo showing the completed remediation.
      </Typography>
      <Button variant="outlined" size="small" component="label">
        Choose photos
        <input
          type="file"
          hidden
          multiple
          accept="image/*"
          onChange={(e) => onChange(Array.from(e.target.files || []))}
        />
      </Button>
      {files.length > 0 && (
        <Typography variant="caption" sx={{ ml: 1.5 }}>
          {files.length} file{files.length === 1 ? '' : 's'} selected — {files.map((f) => f.name).join(', ')}
        </Typography>
      )}
    </Box>
  );
}

// Panel showing per-store notes from the store's latest walkthrough.
// Skip if there's nothing to show. Auto-expanded for single-store scope,
// collapsed by default for multi-store (DO/Basecamp) views.
function StoreNotesPanel({ notes, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  useEffect(() => { setOpen(defaultOpen); }, [defaultOpen]);
  const filtered = (notes || []).filter((n) => n.strengths || n.opportunities || (n.sectionNotes && n.sectionNotes.length));
  if (filtered.length === 0) return null;

  return (
    <Paper variant="outlined" sx={{ mb: 2 }}>
      <Box
        onClick={() => setOpen((v) => !v)}
        sx={{ px: 2, py: 1.5, display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer', bgcolor: 'grey.50', borderBottom: open ? '1px solid' : 'none', borderColor: 'divider' }}
      >
        <IconButton size="small">{open ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}</IconButton>
        <Typography variant="subtitle2">
          Latest walkthrough notes <Typography component="span" variant="body2" color="text.secondary">({filtered.length} store{filtered.length === 1 ? '' : 's'})</Typography>
        </Typography>
      </Box>
      <Collapse in={open}>
        <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
          {filtered.map((entry) => (
            <Box key={entry.storeId} sx={{ borderLeft: '3px solid', borderColor: 'primary.main', pl: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                {entry.storeName}
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  {entry.walkthroughDate ? fmtDate(entry.walkthroughDate) : ''}
                </Typography>
              </Typography>
              {entry.strengths && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>Strengths</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{entry.strengths}</Typography>
                </Box>
              )}
              {entry.opportunities && (
                <Box sx={{ mb: 1 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>Opportunities</Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{entry.opportunities}</Typography>
                </Box>
              )}
              {entry.sectionNotes?.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={600}>Section notes</Typography>
                  {entry.sectionNotes.map((n) => (
                    <Box key={n.sectionNum} sx={{ mt: 0.5 }}>
                      <Typography variant="caption" fontWeight={600}>{n.sectionNum}. {n.sectionName}</Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{n.text}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </Collapse>
    </Paper>
  );
}

export function ItemDrawer({ open, frId, onClose, userEmail, onSaved }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({ status: 'Open', estimatedCorrectionDate: null, correctionSteps: '', note: '' });
  const [completionFiles, setCompletionFiles] = useState([]);

  const { user: firebaseUser } = useUserAuth();

  const load = useCallback(async () => {
    if (!frId) return;
    setLoading(true);
    setError(null);
    try {
      const token = await firebaseUser.getIdToken(true);
      const { data } = await axios.get(`/hd-api/facilities-review/items/${frId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDetail(data);
      setForm({
        status: data.item.status || 'Open',
        estimatedCorrectionDate: data.item.estimatedCorrectionDate ? new Date(data.item.estimatedCorrectionDate) : null,
        correctionSteps: data.item.correctionSteps || '',
        note: '',
      });
    } catch (e) {
      setError('Failed to load item detail.');
    } finally {
      setLoading(false);
    }
  }, [frId, firebaseUser]);

  useEffect(() => { if (open && frId) load(); }, [open, frId, load]);

  async function handleSubmit() {
    setSaving(true);
    setError(null);
    try {
      const token = await firebaseUser.getIdToken(true);
      const body = {
        status: form.status,
        estimatedCorrectionDate: form.estimatedCorrectionDate
          ? format(form.estimatedCorrectionDate, 'yyyy-MM-dd')
          : null,
        correctionSteps: form.correctionSteps || null,
        note: form.note || null,
        updatedBy: userEmail,
      };
      await axios.post(`/hd-api/facilities-review/items/${frId}/action`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (form.status === 'Completed' && completionFiles.length > 0) {
        const fd = new FormData();
        completionFiles.forEach((f) => fd.append('files', f));
        await axios.post(`/hd-api/facilities-review/items/${frId}/completion-photos`, fd, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
        });
      }

      await load();
      setForm((f) => ({ ...f, note: '' }));
      setCompletionFiles([]);
      if (onSaved) onSaved();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save update.');
    } finally {
      setSaving(false);
    }
  }

  const item = detail?.item;
  const images = detail?.images || [];
  const updatesLog = (item?.updatesLog || []).slice().reverse();

  return (
    <Drawer anchor="right" open={open} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', md: 640 } } }}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center', borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" sx={{ flex: 1 }}>Update Action Item</Typography>
        <IconButton onClick={onClose}><CloseIcon /></IconButton>
      </Box>

      <Box sx={{ p: 2 }}>
        {loading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>}
        {!loading && error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!loading && item && (
          <>
            <Typography variant="subtitle2" gutterBottom>Current Status</Typography>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Store</Typography>
                  <Typography variant="body2">{item.storeName}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Owner</Typography>
                  <Typography variant="body2">{item.owner}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Area</Typography>
                  <Typography variant="body2">{item.area}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Item</Typography>
                  <Typography variant="body2">{item.item}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">Issue</Typography>
                  <Typography variant="body2">{item.issue}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Current status</Typography>
                  <Box sx={{ mt: 0.5 }}><StatusChip status={item.status} /></Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Estimated correction date</Typography>
                  <Typography variant="body2">{fmtDate(item.estimatedCorrectionDate)}</Typography>
                </Grid>
              </Grid>
            </Paper>

            {images.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <PhotoGroup label="Issue photos" images={images.filter((i) => (i.photoType || 'issue') === 'issue')} />
                <PhotoGroup label="Completion photos" images={images.filter((i) => i.photoType === 'completion')} />
              </Box>
            )}

            <Typography variant="subtitle2" gutterBottom>Task Updates</Typography>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Status update</InputLabel>
                    <Select
                      value={form.status}
                      label="Status update"
                      onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
                    >
                      {['Open', 'In Progress', 'Completed'].map((s) => (
                        <MenuItem key={s} value={s}>{s}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    selected={form.estimatedCorrectionDate}
                    onChange={(d) => setForm((f) => ({ ...f, estimatedCorrectionDate: d }))}
                    dateFormat="MMM d, yyyy"
                    customInput={<TextField size="small" label="Estimated correction date" fullWidth autoComplete="off" />}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    size="small"
                    label="Correction steps"
                    value={form.correctionSteps}
                    onChange={(e) => setForm((f) => ({ ...f, correctionSteps: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    size="small"
                    label="Update note (added to history)"
                    value={form.note}
                    onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                  />
                </Grid>
                {form.status === 'Completed' && (
                  <Grid item xs={12}>
                    <CompletionPhotoInput
                      files={completionFiles}
                      onChange={setCompletionFiles}
                    />
                  </Grid>
                )}
                <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <Button variant="contained" onClick={handleSubmit} disabled={saving} startIcon={saving ? <CircularProgress size={14} /> : null}>
                    Submit
                  </Button>
                </Grid>
              </Grid>
            </Paper>

            {updatesLog.length > 0 && (
              <>
                <Typography variant="subtitle2" gutterBottom>Update history</Typography>
                <Paper variant="outlined" sx={{ p: 1 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>When</TableCell>
                        <TableCell>By</TableCell>
                        <TableCell>Change</TableCell>
                        <TableCell>Note</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {updatesLog.map((u, i) => (
                        <TableRow key={i}>
                          <TableCell>{fmtDateTime(u.at)}</TableCell>
                          <TableCell>{u.by}</TableCell>
                          <TableCell>
                            {u.statusFrom !== u.statusTo && (
                              <Typography variant="caption" display="block">
                                {u.statusFrom} → {u.statusTo}
                              </Typography>
                            )}
                            {String(u.etaFrom || '') !== String(u.etaTo || '') && (
                              <Typography variant="caption" color="text.secondary" display="block">
                                ETA: {fmtDate(u.etaFrom)} → {fmtDate(u.etaTo)}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ maxWidth: 220 }}>{u.note || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Paper>
              </>
            )}
          </>
        )}
      </Box>
    </Drawer>
  );
}

export default function FacilitiesReview() {
  const { user: firebaseUser } = useUserAuth();
  const isTabletOrMobile = useMediaQuery({ maxWidth: 1224 });

  const [userInfo, setUserInfo] = useState(null);
  const [rows, setRows] = useState([]);
  const [storeNotes, setStoreNotes] = useState([]);
  const [scope, setScope] = useState(null); // { role, kind, allowedCount, appliedCount }
  const [dimensions, setDimensions] = useState({ owners: [], areas: [], dirOps: [], statuses: [], stores: [] });
  const [selectedStoreIds, setSelectedStoreIds] = useState([]);
  const [filters, setFilters] = useState({ owner: '', area: '', status: '', dirOps: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [drawerFrId, setDrawerFrId] = useState(null);
  const drawerOpen = drawerFrId !== null;

  const getToken = useCallback(async () => {
    if (!firebaseUser?.getIdToken) return null;
    return firebaseUser.getIdToken(true);
  }, [firebaseUser]);

  const loadUserInfo = useCallback(async () => {
    if (!firebaseUser?.email) return;
    const token = await getToken();
    if (!token) return;
    const { data } = await axios.get(`/hd-api/user/${firebaseUser.email}/info`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setUserInfo(data.userInfo);
  }, [firebaseUser, getToken]);

  const loadDimensions = useCallback(async () => {
    if (!firebaseUser?.email) return;
    const token = await getToken();
    if (!token) return;
    const { data } = await axios.get(
      `/hd-api/facilities-review/dimensions?user=${encodeURIComponent(firebaseUser.email)}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    setDimensions(data);
  }, [firebaseUser, getToken]);

  const loadItems = useCallback(async () => {
    if (!firebaseUser?.email) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const params = new URLSearchParams({ user: firebaseUser.email });
      if (selectedStoreIds.length > 0) params.set('storeIds', selectedStoreIds.join(','));
      if (filters.owner) params.set('owner', filters.owner);
      if (filters.area) params.set('area', filters.area);
      if (filters.status) params.set('status', filters.status);
      if (filters.dirOps) params.set('dirOps', filters.dirOps);
      const { data } = await axios.get(`/hd-api/facilities-review/open-items?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(data.rows || []);
      setStoreNotes(data.notes || []);
      setScope(data.scope || null);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load open items.');
    } finally {
      setLoading(false);
    }
  }, [firebaseUser, getToken, selectedStoreIds, filters.owner, filters.area, filters.status, filters.dirOps]);

  useEffect(() => { loadUserInfo(); }, [loadUserInfo]);
  useEffect(() => { loadDimensions(); }, [loadDimensions]);
  useEffect(() => { loadItems(); }, [loadItems]);

  // Group by owner dynamically so any owner values beyond Store/Facilities
  // render as their own table instead of falling into a catch-all bucket.
  // Sort: Store first, Facilities second, then everything else alphabetically.
  const rowsByOwner = useMemo(() => {
    const groups = new Map();
    rows.forEach((r) => {
      const key = r.owner || 'Unassigned';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key).push(r);
    });
    const preferred = ['Store', 'Facilities'];
    const orderedKeys = [
      ...preferred.filter((k) => groups.has(k)),
      ...[...groups.keys()].filter((k) => !preferred.includes(k)).sort(),
    ];
    return orderedKeys.map((owner) => ({ owner, rows: groups.get(owner) }));
  }, [rows]);

  // Show the Store column whenever the caller can see multiple stores.
  const isMultiStore = (scope?.kind === 'district' || scope?.kind === 'all');
  const scopeLabel = !scope
    ? ''
    : scope.kind === 'store'
      ? 'Store'
      : scope.kind === 'district'
        ? `District (${scope.appliedCount}/${scope.allowedCount} stores)`
        : `All stores (${scope.appliedCount}/${scope.allowedCount})`;

  return (
    <div>
      <Header isTabletOrMobile={isTabletOrMobile} />
      <Container maxWidth="xl">
        <Logo isTabletOrMobile={isTabletOrMobile} />

        <Box sx={{ mt: 2 }}>
          <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', mb: 2 }}>
            <Box sx={{
              backgroundColor: 'white',
              borderLeft: (theme) => `4px solid ${theme.palette.secondary.main}`,
              px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 2,
            }}>
              <Box sx={{
                backgroundColor: (theme) => alpha(theme.palette.secondary.main, 0.1),
                borderRadius: 2, p: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <HomeRepairServiceIcon sx={{ fontSize: 32, color: 'secondary.main' }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" sx={{ fontWeight: 600, fontFamily: 'Lato' }}>
                  Facilities Walkthrough - Action Items
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {scope?.kind === 'store' ? (userInfo?.storeName || ' ') : ' '}
                  {scopeLabel && (
                    <Chip
                      size="small"
                      variant="outlined"
                      label={scopeLabel}
                      sx={{ ml: scope?.kind === 'store' ? 1 : 0 }}
                    />
                  )}
                </Typography>
              </Box>
              <Button variant="outlined" size="small" onClick={loadItems} disabled={loading} startIcon={<RefreshIcon />}>
                Refresh
              </Button>
            </Box>
          </Paper>

          <Paper elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', mb: 2, p: 2 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
              {/* Store multi-select — only for DO/Basecamp, hidden for Store role. */}
              {userInfo?.role !== 'Store' && (dimensions.stores?.length || 0) > 1 && (
                <Autocomplete
                  multiple
                  size="small"
                  sx={{ minWidth: 280, flex: 1, maxWidth: 560 }}
                  options={dimensions.stores}
                  getOptionLabel={(o) => o.storeName || String(o.storeId)}
                  isOptionEqualToValue={(a, b) => Number(a.storeId) === Number(b.storeId)}
                  value={dimensions.stores.filter((s) => selectedStoreIds.includes(Number(s.storeId)))}
                  onChange={(_, next) => setSelectedStoreIds(next.map((s) => Number(s.storeId)))}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={selectedStoreIds.length === 0 ? 'Stores (all)' : `Stores (${selectedStoreIds.length})`}
                      placeholder="Filter stores"
                    />
                  )}
                  renderTags={(value, getTagProps) =>
                    value.length <= 3
                      ? value.map((option, index) => (
                          <Chip
                            size="small"
                            label={option.storeName}
                            {...getTagProps({ index })}
                            key={option.storeId}
                          />
                        ))
                      : [<Chip key="count" size="small" label={`${value.length} selected`} />]
                  }
                />
              )}
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Owner</InputLabel>
                <Select value={filters.owner} label="Owner" onChange={(e) => setFilters((f) => ({ ...f, owner: e.target.value }))}>
                  <MenuItem value=""><em>All</em></MenuItem>
                  {(dimensions.owners || []).map((o) => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel>Area</InputLabel>
                <Select value={filters.area} label="Area" onChange={(e) => setFilters((f) => ({ ...f, area: e.target.value }))}>
                  <MenuItem value=""><em>All</em></MenuItem>
                  {(dimensions.areas || []).map((a) => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel>Status</InputLabel>
                <Select value={filters.status} label="Status" onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}>
                  <MenuItem value=""><em>All open</em></MenuItem>
                  {['Open', 'In Progress', 'Completed'].map((s) => (
                    <MenuItem key={s} value={s}>{s}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {isMultiStore && (dimensions.dirOps?.length || 0) > 0 && (
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel>DO</InputLabel>
                  <Select value={filters.dirOps} label="DO" onChange={(e) => setFilters((f) => ({ ...f, dirOps: e.target.value }))}>
                    <MenuItem value=""><em>All</em></MenuItem>
                    {dimensions.dirOps.map((d) => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                  </Select>
                </FormControl>
              )}
              {(selectedStoreIds.length > 0 || filters.owner || filters.area || filters.status || filters.dirOps) && (
                <Button
                  size="small"
                  onClick={() => {
                    setSelectedStoreIds([]);
                    setFilters({ owner: '', area: '', status: '', dirOps: '' });
                  }}
                >
                  Clear filters
                </Button>
              )}
            </Box>
          </Paper>

          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
          ) : (
            <>
              <StoreNotesPanel notes={storeNotes} defaultOpen={false} />

              {rowsByOwner.map(({ owner, rows: groupRows }) => (
                <ItemsTable
                  key={owner}
                  title={`Facilities Issues - ${owner}`}
                  rows={groupRows}
                  onOpen={setDrawerFrId}
                  ownerColor="#3f6fb0"
                  showStore={isMultiStore}
                />
              ))}
              {rows.length === 0 && (
                <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    {isMultiStore ? 'No open action items in this scope.' : 'No open action items for this store.'}
                  </Typography>
                </Paper>
              )}
            </>
          )}
        </Box>
      </Container>

      <ItemDrawer
        open={drawerOpen}
        frId={drawerFrId}
        userEmail={firebaseUser?.email}
        onClose={() => setDrawerFrId(null)}
        onSaved={loadItems}
      />
    </div>
  );
}
